import { $http } from '@/common/api/http.service';
import { useAuthStore } from '@/stores/auth';
import { ref } from 'vue';

/**
 * Records patient SMS reminder consent events (issue #4071, FR-4).
 *
 * The backend appends an immutable PatientConsentEvent and updates the patient's
 * ContactPoint-smsReminderConsent extension; the returned `consent` block reflects the new
 * derived state so callers can refresh the patient without recomputing.
 */
export function useSmsConsent() {
    const loading = ref(false);
    const error = ref(null);
    const authStore = useAuthStore();

    const consentUrl = (patientId) => `/api/account/${authStore.account.id}/patient/${patientId}/sms-consent`;

    /**
     * Record a consent action against a specific telecom value.
     *
     * @param {string} patientId       FHIR Patient id
     * @param {Object} payload
     * @param {string} payload.telecomValue         phone number the consent applies to
     * @param {string} payload.action               granted | declined | revoked | reinstated
     * @param {string} payload.method               verbal | digital-form | sms-keyword | staff-revoked
     * @param {string} [payload.channel]            in_person | phone_call | sms | form
     * @param {boolean} [payload.attestationVerbatim] verbal: staff read the script verbatim
     * @returns {Promise<Object>} { consent: { status, method, lastUpdatedAt, suppressReminders, suppressCareMessages } }
     */
    const recordConsentEvent = async (patientId, payload) => {
        loading.value = true;
        error.value = null;
        try {
            const response = await $http.post(consentUrl(patientId), payload);
            return response.data;
        } catch (err) {
            error.value = err;
            console.error('SMS consent record error:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Fetch the clinic's public SMS compliance identity (#5497): the sender name and the
     * privacy/terms URLs the digital consent widget must display. Public endpoint — no auth.
     *
     * @param {string} accountId clinic account id
     * @returns {Promise<Object>} { clinicName, privacyPolicyUrl, termsAndConditionsUrl } (nulls when unset)
     */
    const fetchComplianceUrls = async (accountId) => {
        loading.value = true;
        error.value = null;
        try {
            const response = await $http.get(`/api/account/${accountId}/sms-compliance-urls`);
            return response.data;
        } catch (err) {
            error.value = err;
            console.error('SMS compliance URLs fetch error:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    return {
        loading,
        error,
        recordConsentEvent,
        fetchComplianceUrls,
    };
}
